'use client';

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────
interface BookingStep {
  id: number;
  label: string;
  icon: string;
}

interface TravelerDetail {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  nationality: string;
  passportNumber: string;
  dateOfBirth: string;
}

interface BookingFormState {
  vertical: string;
  destination: string;
  checkIn: string;
  checkOut: string;
  travelers: number;
  budget: string;
  currency: string;
  specialRequests: string;
  couponCode: string;
  couponDiscount: number;
  paymentMethod: string;
  guestMode: boolean;
  guestEmail: string;
  guestName: string;
  guestPhone: string;
  travelerDetails: TravelerDetail[];
  selectedProduct: Record<string, unknown> | null;
  quoteMode: boolean;
}

const STEPS: BookingStep[] = [
  { id: 1, label: 'Search & Select', icon: '🔍' },
  { id: 2, label: 'Traveler Details', icon: '👤' },
  { id: 3, label: 'Review & Pricing', icon: '📋' },
  { id: 4, label: 'Payment', icon: '💳' },
  { id: 5, label: 'Confirmation', icon: '✅' },
];

const TRAVEL_VERTICALS = [
  { id: 'hotel', label: 'Hotels', icon: '🏨' },
  { id: 'flight', label: 'Flights', icon: '✈' },
  { id: 'tour', label: 'Tours', icon: '🗺' },
  { id: 'activity', label: 'Activities', icon: '🎯' },
  { id: 'transfer', label: 'Transfers', icon: '🚗' },
  { id: 'cruise', label: 'Cruises', icon: '🛳' },
  { id: 'rail', label: 'Rail', icon: '🚂' },
  { id: 'car_rental', label: 'Car Rental', icon: '🏎' },
  { id: 'visa', label: 'Visa', icon: '📋' },
  { id: 'insurance', label: 'Insurance', icon: '🛡' },
  { id: 'package', label: 'Packages', icon: '🎁' },
  { id: 'custom_itinerary', label: 'Custom', icon: '✨' },
  { id: 'private_aviation', label: 'Private Jets', icon: '✈' },
  { id: 'luxury_experience', label: 'Experiences', icon: '💎' },
];

const CURRENCIES = ['USD', 'EUR', 'GBP', 'AED', 'SGD', 'INR', 'AUD', 'JPY', 'CHF', 'CAD'];

const PAYMENT_METHODS = [
  { id: 'razorpay', label: 'Card / UPI / Net Banking', icon: '💳', desc: 'Powered by Razorpay' },
  { id: 'paypal', label: 'PayPal', icon: '🅿', desc: 'Pay with PayPal' },
  { id: 'payu', label: 'PayU', icon: '💰', desc: 'PayU Payment Gateway' },
  { id: 'bank_transfer', label: 'Bank Transfer', icon: '🏦', desc: 'Direct bank transfer' },
];

const emptyTraveler = (): TravelerDetail => ({
  firstName: '', lastName: '', email: '', phone: '',
  nationality: '', passportNumber: '', dateOfBirth: '',
});

export default function BookingPageContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { user, profile } = useAuth();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [bookingRef, setBookingRef] = useState('');
  const [products, setProducts] = useState<Record<string, unknown>[]>([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  const [form, setForm] = useState<BookingFormState>({
    vertical: searchParams?.get('vertical') || 'hotel',
    destination: searchParams?.get('destination') || '',
    checkIn: searchParams?.get('checkIn') || '',
    checkOut: searchParams?.get('checkOut') || '',
    travelers: Number(searchParams?.get('travelers') || 2),
    budget: searchParams?.get('budget') || '',
    currency: searchParams?.get('currency') || 'USD',
    specialRequests: '',
    couponCode: '',
    couponDiscount: 0,
    paymentMethod: 'razorpay',
    guestMode: !user,
    guestEmail: '',
    guestName: '',
    guestPhone: '',
    travelerDetails: [emptyTraveler()],
    selectedProduct: null,
    quoteMode: false,
  });

  useEffect(() => {
    if (user && profile) {
      setForm((f) => ({ ...f, guestMode: false }));
    }
  }, [user, profile]);

  useEffect(() => {
    if (form.travelers > form.travelerDetails.length) {
      const diff = form.travelers - form.travelerDetails.length;
      setForm((f) => ({ ...f, travelerDetails: [...f.travelerDetails, ...Array(diff).fill(null).map(emptyTraveler)] }));
    }
  }, [form.travelers]);

  const searchProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const params = new URLSearchParams({ vertical: form.vertical, limit: '12' });
      const res = await fetch(`/api/supplier-products?${params}`);
      const data = await res.json();
      if (data?.data) setProducts(data.data);
    } catch {}
    setProductsLoading(false);
  }, [form.vertical]);

  useEffect(() => { searchProducts(); }, [searchProducts]);

  const applyCoupon = async () => {
    if (!form.couponCode) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const res = await fetch(`/api/promotions/validate?code=${form.couponCode}&vertical=${form.vertical}`);
      const data = await res.json();
      if (data?.data?.discount_value) {
        setForm((f) => ({ ...f, couponDiscount: data.data.discount_value }));
      } else {
        setCouponError('Invalid or expired coupon code');
      }
    } catch {
      setCouponError('Could not validate coupon');
    }
    setCouponLoading(false);
  };

  const calculateTotal = () => {
    const base = (form.selectedProduct as Record<string, unknown> | null)?.base_price as number || 0;
    const subtotal = base * form.travelers;
    const discount = form.couponDiscount > 0 ? (subtotal * form.couponDiscount) / 100 : 0;
    return { subtotal, discount, total: subtotal - discount };
  };

  const handleSubmitBooking = async () => {
    setLoading(true);
    try {
      const { total } = calculateTotal();
      const res = await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          service_type: form.vertical,
          service_details: {
            destination: form.destination,
            checkIn: form.checkIn,
            checkOut: form.checkOut,
            product: form.selectedProduct,
          },
          total_amount: total,
          currency: form.currency,
          traveler_details: form.travelerDetails,
          notes: form.specialRequests,
          metadata: {
            coupon_code: form.couponCode,
            coupon_discount: form.couponDiscount,
            payment_method: form.paymentMethod,
            guest_mode: form.guestMode,
            guest_email: form.guestEmail,
          },
        }),
      });
      const data = await res.json();
      if (data?.data?.id) {
        setBookingRef(data.data.id.slice(0, 8).toUpperCase());
        setStep(5);
      }
    } catch {}
    setLoading(false);
  };

  const handleQuoteRequest = async () => {
    setLoading(true);
    try {
      await fetch('/api/quote-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vertical: form.vertical,
          requirements: { destination: form.destination, checkIn: form.checkIn, checkOut: form.checkOut },
          traveler_count: form.travelers,
          currency: form.currency,
          guest_email: form.guestEmail || undefined,
          guest_name: form.guestName || undefined,
          notes: form.specialRequests,
        }),
      });
      setBookingRef('QR-' + Math.random().toString(36).slice(2, 8).toUpperCase());
      setStep(5);
    } catch {}
    setLoading(false);
  };

  const updateTraveler = (idx: number, field: keyof TravelerDetail, value: string) => {
    setForm((f) => {
      const updated = [...f.travelerDetails];
      updated[idx] = { ...updated[idx], [field]: value };
      return { ...f, travelerDetails: updated };
    });
  };

  const { subtotal, discount, total } = calculateTotal();

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Header */}
      <div className="border-b border-[var(--border)] bg-[var(--surface-2)]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase text-lg" style={{ fontFamily: 'var(--font-serif)' }}>
            WorldwayLuxe
          </Link>
          <div className="hidden md:flex items-center gap-2">
            {STEPS.map((s) => (
              <div key={s.id} className="flex items-center gap-1">
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${step === s.id ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : step > s.id ? 'text-[var(--primary)]' : 'text-[var(--muted-foreground)]'}`}>
                  <span>{s.icon}</span>
                  <span className="hidden lg:block">{s.label}</span>
                </div>
                {s.id < STEPS.length && <div className={`w-4 h-px ${step > s.id ? 'bg-[var(--primary)]' : 'bg-[var(--border)]'}`} />}
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {!user && (
              <Link href="/auth/login" className="text-xs text-[var(--primary)] hover:underline">Sign in for member rates</Link>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* ── Step 1: Search & Select ── */}
        {step === 1 && (
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                <span className="gold-text-gradient">Search & Select</span>
              </h1>
              <p className="text-[var(--muted-foreground)]">Choose your travel category and destination</p>
            </div>

            {/* Vertical selector */}
            <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
              {TRAVEL_VERTICALS.map((v) => (
                <button
                  key={v.id}
                  onClick={() => setForm((f) => ({ ...f, vertical: v.id, selectedProduct: null }))}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl text-xs font-medium transition-all border ${form.vertical === v.id ? 'border-[var(--primary)] bg-[rgba(201,168,76,0.08)] text-[var(--primary)]' : 'border-[var(--border)] text-[var(--muted-foreground)] hover:border-[rgba(201,168,76,0.3)] hover:text-[var(--foreground)]'}`}
                >
                  <span className="text-xl">{v.icon}</span>
                  <span>{v.label}</span>
                </button>
              ))}
            </div>

            {/* Search fields */}
            <div className="glass-card rounded-2xl p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1.5">Destination</label>
                  <input type="text" placeholder="City, country or resort" value={form.destination} onChange={(e) => setForm((f) => ({ ...f, destination: e.target.value }))} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                </div>
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1.5">Check In / Departure</label>
                  <input type="date" value={form.checkIn} onChange={(e) => setForm((f) => ({ ...f, checkIn: e.target.value }))} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                </div>
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1.5">Check Out / Return</label>
                  <input type="date" value={form.checkOut} onChange={(e) => setForm((f) => ({ ...f, checkOut: e.target.value }))} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1.5">Travelers</label>
                  <select value={form.travelers} onChange={(e) => setForm((f) => ({ ...f, travelers: Number(e.target.value) }))} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]">
                    {[1,2,3,4,5,6,8,10,12,15,20].map((n) => <option key={n} value={n}>{n} {n === 1 ? 'Traveler' : 'Travelers'}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1.5">Currency</label>
                  <select value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]">
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1.5">Budget (optional)</label>
                  <input type="text" placeholder="e.g. $5,000" value={form.budget} onChange={(e) => setForm((f) => ({ ...f, budget: e.target.value }))} className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                </div>
              </div>
            </div>

            {/* Products */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Available Options</h2>
                <button onClick={() => setForm((f) => ({ ...f, quoteMode: true }))} className="text-xs text-[var(--primary)] hover:underline">
                  Request Custom Quote →
                </button>
              </div>

              {productsLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[1,2,3].map((i) => <div key={i} className="glass-card rounded-2xl h-48 animate-pulse" />)}
                </div>
              ) : products.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {products.map((p) => {
                    const prod = p as Record<string, unknown>;
                    return (
                      <div
                        key={prod.id as string}
                        onClick={() => setForm((f) => ({ ...f, selectedProduct: prod }))}
                        className={`glass-card rounded-2xl p-5 cursor-pointer transition-all ${form.selectedProduct && (form.selectedProduct as Record<string, unknown>).id === prod.id ? 'border-[var(--primary)] gold-glow' : 'hover:border-[rgba(201,168,76,0.3)]'}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="font-semibold text-sm" style={{ fontFamily: 'var(--font-serif)' }}>{prod.name as string}</h3>
                          {form.selectedProduct && (form.selectedProduct as Record<string, unknown>).id === prod.id && (
                            <span className="text-[var(--primary)] text-lg">✓</span>
                          )}
                        </div>
                        <p className="text-xs text-[var(--muted-foreground)] mb-3 line-clamp-2">{prod.description as string}</p>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-lg font-bold text-[var(--primary)]">{form.currency} {(prod.base_price as number)?.toLocaleString()}</span>
                            <span className="text-xs text-[var(--muted-foreground)] ml-1">/ person</span>
                          </div>
                          {prod.rating && <span className="text-xs text-[var(--muted-foreground)]">⭐ {prod.rating as number}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="glass-card rounded-2xl p-10 text-center">
                  <div className="text-4xl mb-3">🔍</div>
                  <p className="text-[var(--muted-foreground)] mb-4">No products found for this category. Request a custom quote from our concierge team.</p>
                  <button onClick={() => setForm((f) => ({ ...f, quoteMode: true }))} className="btn-primary px-6 py-2.5 rounded-lg text-sm">
                    Request Quote
                  </button>
                </div>
              )}
            </div>

            {/* Quote mode */}
            {form.quoteMode && (
              <div className="glass-card rounded-2xl p-6 border border-[rgba(201,168,76,0.2)]">
                <h3 className="font-semibold mb-4 text-[var(--primary)]">✨ Custom Quote Request</h3>
                <textarea
                  placeholder="Describe your ideal experience, special requirements, preferences..."
                  value={form.specialRequests}
                  onChange={(e) => setForm((f) => ({ ...f, specialRequests: e.target.value }))}
                  rows={4}
                  className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)] resize-none mb-4"
                />
                {!user && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <input type="text" placeholder="Your Name" value={form.guestName} onChange={(e) => setForm((f) => ({ ...f, guestName: e.target.value }))} className="bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                    <input type="email" placeholder="Email Address" value={form.guestEmail} onChange={(e) => setForm((f) => ({ ...f, guestEmail: e.target.value }))} className="bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                    <input type="tel" placeholder="Phone Number" value={form.guestPhone} onChange={(e) => setForm((f) => ({ ...f, guestPhone: e.target.value }))} className="bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                  </div>
                )}
                <button onClick={handleQuoteRequest} disabled={loading} className="btn-primary px-8 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50">
                  {loading ? 'Submitting...' : 'Submit Quote Request'}
                </button>
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={() => setStep(2)}
                disabled={!form.selectedProduct && !form.quoteMode}
                className="btn-primary px-10 py-3 rounded-xl text-sm font-semibold disabled:opacity-40"
              >
                Continue to Traveler Details →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 2: Traveler Details ── */}
        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                <span className="gold-text-gradient">Traveler Details</span>
              </h1>
              <p className="text-[var(--muted-foreground)]">Enter details for all {form.travelers} traveler{form.travelers > 1 ? 's' : ''}</p>
            </div>

            {!user && (
              <div className="glass-card rounded-2xl p-5 border border-[rgba(201,168,76,0.2)]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold">Guest Checkout</h3>
                  <Link href="/auth/login" className="text-xs text-[var(--primary)] hover:underline">Sign in for member benefits →</Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input type="text" placeholder="Full Name *" value={form.guestName} onChange={(e) => setForm((f) => ({ ...f, guestName: e.target.value }))} className="bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                  <input type="email" placeholder="Email Address *" value={form.guestEmail} onChange={(e) => setForm((f) => ({ ...f, guestEmail: e.target.value }))} className="bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                  <input type="tel" placeholder="Phone Number" value={form.guestPhone} onChange={(e) => setForm((f) => ({ ...f, guestPhone: e.target.value }))} className="bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]" />
                </div>
              </div>
            )}

            {form.travelerDetails.slice(0, form.travelers).map((t, idx) => (
              <div key={idx} className="glass-card rounded-2xl p-6">
                <h3 className="font-semibold mb-4 text-[var(--primary)]">Traveler {idx + 1} {idx === 0 ? '(Primary)' : ''}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[
                    { field: 'firstName', label: 'First Name', type: 'text', placeholder: 'First Name' },
                    { field: 'lastName', label: 'Last Name', type: 'text', placeholder: 'Last Name' },
                    { field: 'email', label: 'Email', type: 'email', placeholder: 'Email Address' },
                    { field: 'phone', label: 'Phone', type: 'tel', placeholder: 'Phone Number' },
                    { field: 'nationality', label: 'Nationality', type: 'text', placeholder: 'Nationality' },
                    { field: 'passportNumber', label: 'Passport Number', type: 'text', placeholder: 'Passport Number' },
                    { field: 'dateOfBirth', label: 'Date of Birth', type: 'date', placeholder: '' },
                  ].map(({ field, label, type, placeholder }) => (
                    <div key={field}>
                      <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1.5">{label}</label>
                      <input
                        type={type}
                        placeholder={placeholder}
                        value={t[field as keyof TravelerDetail]}
                        onChange={(e) => updateTraveler(idx, field as keyof TravelerDetail, e.target.value)}
                        className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="glass-card rounded-2xl p-5">
              <label className="block text-[10px] tracking-widest uppercase text-[var(--muted-foreground)] mb-1.5">Special Requests</label>
              <textarea
                placeholder="Dietary requirements, accessibility needs, special occasions..."
                value={form.specialRequests}
                onChange={(e) => setForm((f) => ({ ...f, specialRequests: e.target.value }))}
                rows={3}
                className="w-full bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)] resize-none"
              />
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(1)} className="btn-ghost px-8 py-3 rounded-xl text-sm font-semibold">← Back</button>
              <button onClick={() => setStep(3)} className="btn-primary px-10 py-3 rounded-xl text-sm font-semibold">Review Booking →</button>
            </div>
          </div>
        )}

        {/* ── Step 3: Review & Pricing ── */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                <span className="gold-text-gradient">Review & Pricing</span>
              </h1>
              <p className="text-[var(--muted-foreground)]">Review your booking details and apply any promotions</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="glass-card rounded-2xl p-6">
                  <h3 className="font-semibold mb-4">Booking Summary</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Service Type</span><span className="capitalize">{form.vertical.replace('_', ' ')}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Destination</span><span>{form.destination || 'Not specified'}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Check In</span><span>{form.checkIn || 'Flexible'}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Check Out</span><span>{form.checkOut || 'Flexible'}</span></div>
                    <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Travelers</span><span>{form.travelers}</span></div>
                    {form.selectedProduct && (
                      <div className="flex justify-between"><span className="text-[var(--muted-foreground)]">Selected</span><span className="text-[var(--primary)]">{(form.selectedProduct as Record<string, unknown>).name as string}</span></div>
                    )}
                  </div>
                </div>

                <div className="glass-card rounded-2xl p-6">
                  <h3 className="font-semibold mb-4">Promotions & Coupons</h3>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      placeholder="Enter coupon code (e.g. LUXE10)"
                      value={form.couponCode}
                      onChange={(e) => setForm((f) => ({ ...f, couponCode: e.target.value.toUpperCase() }))}
                      className="flex-1 bg-[var(--surface-3)] border border-[var(--border)] rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--primary)]"
                    />
                    <button onClick={applyCoupon} disabled={couponLoading} className="btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50">
                      {couponLoading ? '...' : 'Apply'}
                    </button>
                  </div>
                  {couponError && <p className="text-xs text-[var(--danger)] mt-2">{couponError}</p>}
                  {form.couponDiscount > 0 && <p className="text-xs text-[var(--success)] mt-2">✓ {form.couponDiscount}% discount applied!</p>}
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {['LUXE10', 'SUMMER25', 'HONEYMOON15'].map((code) => (
                      <button key={code} onClick={() => setForm((f) => ({ ...f, couponCode: code }))} className="text-[10px] px-2 py-1 rounded border border-[rgba(201,168,76,0.3)] text-[var(--primary)] hover:bg-[rgba(201,168,76,0.08)]">{code}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-2xl p-6 h-fit border border-[rgba(201,168,76,0.15)]">
                <h3 className="font-semibold mb-4">Price Summary</h3>
                <div className="space-y-3 text-sm mb-6">
                  <div className="flex justify-between">
                    <span className="text-[var(--muted-foreground)]">Base Price × {form.travelers}</span>
                    <span>{form.currency} {subtotal.toLocaleString()}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-[var(--success)]">
                      <span>Coupon Discount</span>
                      <span>-{form.currency} {discount.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="border-t border-[var(--border)] pt-3 flex justify-between font-bold text-base">
                    <span>Total</span>
                    <span className="text-[var(--primary)]">{form.currency} {total.toLocaleString()}</span>
                  </div>
                </div>
                <div className="text-xs text-[var(--muted-foreground)] space-y-1">
                  <div>✓ Free cancellation (subject to policy)</div>
                  <div>✓ Secure payment processing</div>
                  <div>✓ 24/7 concierge support</div>
                </div>
              </div>
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(2)} className="btn-ghost px-8 py-3 rounded-xl text-sm font-semibold">← Back</button>
              <button onClick={() => setStep(4)} className="btn-primary px-10 py-3 rounded-xl text-sm font-semibold">Proceed to Payment →</button>
            </div>
          </div>
        )}

        {/* ── Step 4: Payment ── */}
        {step === 4 && (
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                <span className="gold-text-gradient">Secure Payment</span>
              </h1>
              <p className="text-[var(--muted-foreground)]">Choose your preferred payment method</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                {PAYMENT_METHODS.map((pm) => (
                  <div
                    key={pm.id}
                    onClick={() => setForm((f) => ({ ...f, paymentMethod: pm.id }))}
                    className={`glass-card rounded-2xl p-5 cursor-pointer transition-all ${form.paymentMethod === pm.id ? 'border-[var(--primary)] gold-glow' : 'hover:border-[rgba(201,168,76,0.3)]'}`}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-2xl">{pm.icon}</span>
                      <div className="flex-1">
                        <div className="font-semibold text-sm">{pm.label}</div>
                        <div className="text-xs text-[var(--muted-foreground)]">{pm.desc}</div>
                      </div>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.paymentMethod === pm.id ? 'border-[var(--primary)] bg-[var(--primary)]' : 'border-[var(--border)]'}`}>
                        {form.paymentMethod === pm.id && <div className="w-2 h-2 rounded-full bg-[var(--primary-foreground)]" />}
                      </div>
                    </div>
                  </div>
                ))}

                <div className="glass-card rounded-2xl p-5">
                  <div className="flex items-center gap-3 text-xs text-[var(--muted-foreground)]">
                    <span>🔒</span>
                    <span>Your payment is secured with 256-bit SSL encryption. We never store your card details.</span>
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-2xl p-6 h-fit border border-[rgba(201,168,76,0.15)]">
                <h3 className="font-semibold mb-4">Order Total</h3>
                <div className="text-3xl font-bold text-[var(--primary)] mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                  {form.currency} {total.toLocaleString()}
                </div>
                <div className="text-xs text-[var(--muted-foreground)] mb-6">
                  {form.travelers} traveler{form.travelers > 1 ? 's' : ''} · {form.vertical.replace('_', ' ')}
                </div>
                <button
                  onClick={handleSubmitBooking}
                  disabled={loading}
                  className="w-full btn-primary py-3.5 rounded-xl text-sm font-semibold disabled:opacity-50"
                >
                  {loading ? 'Processing...' : `Pay ${form.currency} ${total.toLocaleString()}`}
                </button>
                <p className="text-[10px] text-[var(--muted-foreground)] text-center mt-3">
                  By proceeding you agree to our Terms of Service and Privacy Policy
                </p>
              </div>
            </div>

            <div className="flex justify-between">
              <button onClick={() => setStep(3)} className="btn-ghost px-8 py-3 rounded-xl text-sm font-semibold">← Back</button>
            </div>
          </div>
        )}

        {/* ── Step 5: Confirmation ── */}
        {step === 5 && (
          <div className="max-w-2xl mx-auto text-center py-16 space-y-6">
            <div className="w-20 h-20 rounded-full bg-[rgba(201,168,76,0.1)] border border-[rgba(201,168,76,0.3)] flex items-center justify-center mx-auto text-4xl">
              ✅
            </div>
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'var(--font-serif)' }}>
              <span className="gold-text-gradient">Booking {form.quoteMode ? 'Request' : 'Confirmed'}!</span>
            </h1>
            <p className="text-[var(--muted-foreground)] text-lg">
              {form.quoteMode
                ? 'Your quote request has been submitted. Our concierge team will contact you within 24 hours.' :'Your booking has been confirmed. A confirmation email will be sent shortly.'}
            </p>
            {bookingRef && (
              <div className="glass-card rounded-2xl p-6 inline-block">
                <div className="text-xs tracking-widest uppercase text-[var(--muted-foreground)] mb-1">Reference Number</div>
                <div className="text-2xl font-bold text-[var(--primary)] font-mono">{bookingRef}</div>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Link href="/customer-portal" className="btn-primary px-8 py-3 rounded-xl text-sm font-semibold">
                View My Bookings
              </Link>
              <Link href="/" className="btn-ghost px-8 py-3 rounded-xl text-sm font-semibold">
                Back to Home
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
